import { lazy, Suspense } from "react"
import { Outlet, useLocation, Navigate } from "react-router-dom"
import { isJobDetails } from "@/src/utils/storage-available"

import { featureFlags } from "src/config/featureFlags"
import { CONFIG } from "src/config-global"
import { DashboardLayout } from "src/layouts/dashboard"
import { LoadingScreen } from "src/common/loading-screen"

import { AuthGuard, InternalUserGuard } from "src/auth/guard"

import Workspaces from "@/src/features/internalUsers/pages/Workspaces"
import CreditsAndVAS from "@/src/features/internalUsers/pages/CreditsAndVAS"
import GenerateOtp from "@/src/features/internalUsers/pages/GenerateOtp"
import JobLeadsDetail from "@/src/features/internalUsers/pages/JobLeadsDetail"
import JobsAndLeads from "@/src/features/internalUsers/pages/JobsAndLeads"
import PromptPolicies from "@/src/features/internalUsers/pages/PromptPolicies"
import TeamAssignments from "@/src/features/internalUsers/pages/TeamAssignments"

import { paths } from "../paths"

// ----------------------------------------------------------------------

// const CandidatesPage = lazy(() => import("@/src/features/candidates/pages/CandidatesPage"))
const CandidatesPage = lazy(() => import("@/src/features/candidates/pages/CandidatesPage"))

const ClientsPage = lazy(() => import("@/src/features/clients/clientsPage"))
const MandatesPage = lazy(() => import("@/src/features/jobPosting/components/mandatesPage"))
const BulkUploadsPage = lazy(() => import("@/src/features/candidates/bulkUploadPage/bulkUploadPage"))
const UserManagementPage = lazy(() => import("@/src/features/userManagement/components/userManagementPage"))
const ClientsForm = lazy(() => import("src/pages/dashboard/newClientsPage"))
// Removed - now using JobPostingFormPage from postJob routes
const ClientDetailsPage = lazy(() => import("@/src/features/clients/clientsDetailsPage"))
const MandateDetailsPage = lazy(() => import("@/src/features/jobPosting/components/mandatesDetailsPage"))
const JobDetailsPage = lazy(() => import("@/src/features/jobDetails/jobDetailsPage"))
const OnboardingPage = lazy(() => import("src/pages/dashboard/onboarding/onboarding"))
const PublicApplyJobV2Page = lazy(() => import("src/pages/dashboard/publicApplyJobV2/publicApplyJobV2Page"))
const PublicApplyReschedulePage = lazy(() => import("src/pages/dashboard/publicApplyJobV2/PublicApplyReschedulePage"))
// const CreditsPage = lazy(() => import("@/src/features/credits/components/creditsPage"))
const CreditsPage = lazy(() => import("@/src/features/credits/CreditsDashboard"))
const QuestionBankPage = lazy(() => import("@/src/features/questionBank/pages/QuestionBankPage"))
// ----------------------------------------------------------------------

const LayoutContent = () => {
    const location = useLocation()

    const layout = (
        <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
                <Outlet key={location.pathname} />
            </Suspense>
        </DashboardLayout>
    )
    // Public apply routes and job details should never require auth
    if (isJobDetails() || CONFIG.auth.skip) {
        return <>{layout}</>
    }
    return (
        <AuthGuard>
            <InternalUserGuard>{layout}</InternalUserGuard>
        </AuthGuard>
    )
}

export const dashboardRoutes = (isAiProcessing: boolean, disableAddCandidates: boolean = false) => [
    {
        path: "dashboard",
        element: <LayoutContent />,
        children: [
            { element: <MandatesPage />, index: true },
            // { path: paths.dashboard.interviews, element: <InterviewsPage /> },
            // { path: paths.dashboard.interviewForm, element: <InterviewsForm /> },
            // { path: paths.dashboard.leadForm, element: <LeadsForm /> },
            { path: paths.dashboard.clients, element: <ClientsPage /> },
            // { path: paths.dashboard.selections, element: <SelectionsPage /> },

            ...(!disableAddCandidates ? [{ path: paths.dashboard.onboarding, element: <OnboardingPage /> }] : []),
            { path: paths.dashboard.clientForm, element: <ClientsForm /> },
            // { path: paths.dashboard.selectionForm, element: <SelectionForm /> },
            {
                path: paths.dashboard.mandateForm,
                element: <Navigate to={paths.postJob.jobDescription} replace />,
            },
            ...(!disableAddCandidates
                ? [
                      {
                          path: paths.dashboard.bulkUpload,
                          element: <BulkUploadsPage />,
                      },
                  ]
                : []),
            { path: paths.dashboard.userManagement, element: <UserManagementPage /> },
            { path: paths.dashboard.publicApplyWeb, element: <PublicApplyJobV2Page /> },
            { path: paths.dashboard.publicApplyReschedule, element: <PublicApplyReschedulePage /> },
            { path: paths.dashboard.publicApplyPhone, element: <PublicApplyJobV2Page /> },
            { path: paths.dashboard.jobOverview, element: <JobDetailsPage /> },
            { path: paths.dashboard.client.root, element: <ClientDetailsPage /> },
            { path: paths.dashboard.mandate.root, element: <MandateDetailsPage /> },
            //{ path: paths.dashboard.selection.root, element: <SelectionDetailsPage /> },
            { path: paths.dashboard.credits.root, element: <CreditsPage /> },
            { path: paths.dashboard.questionBank, element: <QuestionBankPage /> },
            // Route that handles dashboard with jobId parameter
            { path: paths.dashboard.jobWithId, element: <CandidatesPage /> },
        ],
    },
    {
        path: "internal-user",
        element: <LayoutContent />,
        children: [
            { path: paths.internalUser.root, element: <Workspaces /> },
            { path: paths.internalUser.workspaces, element: <Workspaces /> },
            { path: paths.internalUser.credits, element: <CreditsAndVAS /> },
            { path: "jobs-and-leads/:jobId", element: <JobLeadsDetail /> },
            { path: paths.internalUser.jobsAndLeads, element: <JobsAndLeads /> },
            { path: paths.internalUser.generateOtp, element: <GenerateOtp /> },
            { path: paths.internalUser.teamAssignments, element: <TeamAssignments /> },
            ...(featureFlags.superAdminPromptPolicies
                ? [{ path: paths.internalUser.prompts, element: <PromptPolicies /> }]
                : [
                      {
                          path: paths.internalUser.prompts,
                          element: <Navigate to={paths.internalUser.workspaces} replace />,
                      },
                  ]),
        ],
    },
]
